import React from 'react';
import {
    X, CalendarIcon, CalendarRange, Camera,
    Upload, FileText, Flame, Leaf, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import HelpTooltip from '@/components/HelpTooltip';

const DataEntryForm = ({
    formData,
    setFormData,
    handleSubmit,
    editingEntry,
    setEditingEntry,
    setShowForm,
    flatCategories,
    groupedCategories,
    categoriesLoading,
    handleCategoryChange,
    helpContent,
    getFinancialYear,
    getReportingPeriods,
    getFYLabel,
    formReceiptInputRef,
    formCanvasRef,
    formVideoRef,
    formCameraMode,
    formCapturedImage,
    setFormCapturedImage,
    startFormCamera,
    stopFormCamera,
    captureFormPhoto
}) => {
    return (
        <form onSubmit={handleSubmit} className="space-y-4 bg-[#F8F9FA] p-4 md:p-6 rounded-xl" data-testid="emission-form">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-[#003B5C]">
                    {editingEntry ? 'Edit Entry' : 'Add New Entry'}
                </h3>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        setShowForm(false);
                        setEditingEntry(null);
                        setFormData({ category: '', quantity: '', start_date: new Date().toISOString().split('T')[0], end_date: '', is_date_range: false, entry_date: new Date().toISOString().split('T')[0], receipt_file: null, notes: '' });
                    }}
                    className="text-slate-500 hover:text-slate-700"
                >
                    <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <Label htmlFor="category" className="text-[#003B5C] mb-2 flex items-center text-sm md:text-base">
                        Category *
                        <HelpTooltip {...helpContent.category} />
                    </Label>
                    <Select value={formData.category} onValueChange={handleCategoryChange}>
                        <SelectTrigger className="h-12 text-base" data-testid="category-select">
                            <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[400px]">
                            {categoriesLoading ? (
                                <div className="p-4 text-center text-slate-500 text-sm">Loading categories...</div>
                            ) : (
                                Object.entries(groupedCategories).map(([group, items]) => (
                                    <div key={group}>
                                        <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                                            {group}
                                        </div>
                                        {items.map((cat) => {
                                            const Icon = cat.icon;
                                            return (
                                                <SelectItem key={cat.value} value={cat.value} className="py-2.5 px-3 focus:bg-slate-50">
                                                    <div className="flex flex-col w-full text-left">
                                                        <div className="flex items-center justify-between w-full">
                                                            <div className="flex items-center gap-2">
                                                                {Icon && <Icon className="w-4 h-4" style={{ color: cat.color }} />}
                                                                <span className="font-semibold text-slate-800 text-sm md:text-base">
                                                                    {cat.label}
                                                                    <span className="text-slate-400 font-normal ml-1">({cat.unit})</span>
                                                                </span>
                                                            </div>
                                                            {cat.zeroEmission && (
                                                                <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
                                                                    Zero
                                                                </span>
                                                            )}
                                                        </div>
                                                        {(cat.rawRegion || cat.rawNote) && (
                                                            <div className="text-[11px] md:text-xs text-slate-500 mt-1 ml-6 leading-tight">
                                                                <span className="font-medium text-slate-400 capitalize">{cat.rawRegion}</span>
                                                                {cat.rawNote && (
                                                                    <span className="before:content-['•'] before:mx-1.5 before:text-slate-300">
                                                                        {cat.rawNote}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                    </div>
                                ))
                            )}
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <Label htmlFor="quantity" className="text-[#003B5C] mb-2 flex items-center text-sm md:text-base">
                        Quantity {flatCategories.find(c => c.value === formData.category)?.unit && (
                            <span className="text-slate-500 ml-1">({flatCategories.find(c => c.value === formData.category)?.unit})</span>
                        )} *
                        <HelpTooltip {...helpContent.quantity} />
                    </Label>
                    <Input
                        id="quantity"
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        className="h-12 text-base"
                        required
                        data-testid="quantity-input"
                    />
                </div>

                <div className="md:col-span-3">
                    <div className="bg-white rounded-lg p-3 border border-slate-200 mb-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                {formData.is_date_range ? (
                                    <CalendarRange className="w-4 h-4 text-[#003B5C]" />
                                ) : (
                                    <CalendarIcon className="w-4 h-4 text-[#003B5C]" />
                                )}
                                <Label className="text-slate-700 font-medium text-sm">
                                    {formData.is_date_range ? 'Date Range' : 'Single Date'}
                                </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="text-xs text-slate-500">Point-in-time</span>
                                <Switch
                                    checked={formData.is_date_range}
                                    onCheckedChange={(checked) => setFormData({
                                        ...formData,
                                        is_date_range: checked,
                                        end_date: checked ? formData.end_date || formData.start_date : ''
                                    })}
                                />
                                <span className="text-xs text-slate-500">Period</span>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                            {formData.is_date_range
                                ? 'Use for invoices/bills covering a period (e.g., monthly electricity)'
                                : 'Use for single-day activities (e.g., fuel purchase, flight)'
                            }
                        </p>
                    </div>

                    <div className={formData.is_date_range ? 'grid grid-cols-2 gap-3' : ''}>
                        <div>
                            <Label htmlFor="start_date" className="text-[#003B5C] mb-2 flex items-center text-sm md:text-base">
                                {formData.is_date_range ? 'Start Date' : 'Activity Date'}
                                <HelpTooltip {...helpContent.entryDate} />
                            </Label>
                            <Input
                                id="start_date"
                                type="date"
                                value={formData.start_date}
                                onChange={(e) => setFormData({ ...formData, start_date: e.target.value, entry_date: e.target.value })}
                                className="h-12 text-base"
                                required
                                data-testid="date-input"
                            />
                        </div>

                        {formData.is_date_range && (
                            <div>
                                <Label htmlFor="end_date" className="text-[#003B5C] mb-2 text-sm md:text-base">
                                    End Date
                                </Label>
                                <Input
                                    id="end_date"
                                    type="date"
                                    value={formData.end_date}
                                    min={formData.start_date}
                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                    className="h-12 text-base"
                                />
                            </div>
                        )}
                    </div>

                    <div className="text-xs text-slate-500 bg-blue-50 p-2 rounded border border-blue-100 mt-3">
                        {formData.is_date_range && formData.end_date &&
                            getFinancialYear(new Date(formData.start_date)) !== getFinancialYear(new Date(formData.end_date)) ? (
                            <span className="flex items-center">
                                <CalendarRange className="w-3 h-3 mr-1 text-blue-600" />
                                <span className="text-blue-700">
                                    This entry spans multiple FYs: <strong>{getReportingPeriods(formData.start_date, formData.end_date)}</strong>
                                    <br />
                                    Emissions will be pro-rata allocated to each financial year.
                                </span>
                            </span>
                        ) : (
                            <span>
                                Reporting Period: <strong>{getFYLabel(getFinancialYear(new Date(formData.start_date)))}</strong>
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-4">
                <Label className="text-[#003B5C] mb-3 block text-sm md:text-base">
                    Upload Bill/Receipt (optional)
                </Label>

                <input
                    type="file"
                    ref={formReceiptInputRef}
                    onChange={(e) => {
                        setFormData({ ...formData, receipt_file: e.target.files[0] });
                        setFormCapturedImage(null);
                    }}
                    accept="image/*,application/pdf,.pdf"
                    className="hidden"
                    id="receipt-upload"
                />

                <canvas ref={formCanvasRef} className="hidden" />

                {formCameraMode && !formCapturedImage && (
                    <div className="space-y-3">
                        <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]">
                            <video
                                ref={formVideoRef}
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="flex justify-center space-x-3">
                            <Button
                                type="button"
                                onClick={stopFormCamera}
                                variant="outline"
                                className="border-slate-300"
                            >
                                <X className="w-4 h-4 mr-2" />
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                onClick={captureFormPhoto}
                                className="bg-[#003B5C] hover:bg-[#002A45] text-white"
                            >
                                <Camera className="w-4 h-4 mr-2" />
                                Capture
                            </Button>
                        </div>
                    </div>
                )}

                {formData.receipt_file && !formCameraMode && (
                    <div className="text-center py-4 bg-slate-50 rounded-lg">
                        <div className="w-12 h-12 bg-[#74A57F]/20 rounded-full flex items-center justify-center mx-auto mb-2">
                            <FileText className="w-6 h-6 text-[#74A57F]" />
                        </div>
                        <p className="text-sm font-medium text-[#003B5C]">{formData.receipt_file.name}</p>
                        <p className="text-xs text-slate-500 mt-1">
                            {(formData.receipt_file.size / 1024).toFixed(1)} KB
                        </p>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                                e.preventDefault();
                                setFormData({ ...formData, receipt_file: null });
                                setFormCapturedImage(null);
                                if (formReceiptInputRef.current) formReceiptInputRef.current.value = '';
                            }}
                            className="text-red-500 hover:text-red-700 mt-2 text-xs"
                        >
                            Remove
                        </Button>
                    </div>
                )}

                {!formData.receipt_file && !formCameraMode && (
                    <div className="grid grid-cols-2 gap-3">
                        <div
                            onClick={() => formReceiptInputRef.current?.click()}
                            className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#74A57F] hover:bg-[#74A57F]/5 transition-colors"
                        >
                            <div className="flex flex-col items-center">
                                <Upload className="w-8 h-8 text-slate-400 mb-2" />
                                <span className="text-slate-700 font-medium text-sm">Select File</span>
                                <span className="text-xs text-slate-400 mt-1">PDF, JPG, PNG</span>
                            </div>
                        </div>

                        <div
                            onClick={startFormCamera}
                            className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#003B5C] hover:bg-[#003B5C]/5 transition-colors"
                        >
                            <div className="flex flex-col items-center">
                                <Camera className="w-8 h-8 text-slate-400 mb-2" />
                                <span className="text-slate-700 font-medium text-sm">Take Photo</span>
                                <span className="text-xs text-slate-400 mt-1">Use camera</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div>
                <Label htmlFor="notes" className="text-[#003B5C] mb-2 flex items-center text-sm md:text-base">
                    Reference Notes (optional)
                    <HelpTooltip {...helpContent.notes} />
                </Label>
                <Input
                    id="notes"
                    type="text"
                    placeholder="e.g., Invoice #12345, Fuel purchase at Shell"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="h-12 text-base"
                    data-testid="notes-input"
                />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
                <Button
                    type="submit"
                    className="bg-[#003B5C] text-white hover:bg-[#002A42] h-12 text-base w-full sm:w-auto order-1"
                    data-testid="submit-entry-button"
                >
                    {editingEntry ? 'Update Entry' : 'Save Entry'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                        setShowForm(false);
                        setFormData({ category: '', quantity: '', start_date: new Date().toISOString().split('T')[0], end_date: '', is_date_range: false, entry_date: new Date().toISOString().split('T')[0], receipt_file: null, notes: '' });
                    }}
                    className="h-12 text-base w-full sm:w-auto order-2"
                    data-testid="cancel-entry-button"
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
};

export default DataEntryForm;
